/**
 * Operator-authored rich text (Tiptap HTML) — the emptiness check and the sanitizer.
 *
 * Pure functions (ADR 010 / sharing.md § 3): no I/O, no writes, safe in `useMemo`.
 * Lives in `@auctogon/core` rather than `core-admin` because BOTH apps render the
 * same operator-authored content: admin-web writes it and shows it on the invoice
 * screen, the PDF, the Buyer's Guide and the invoice email; bidder-web shows the
 * after-sale fine print on a buyer's invoice card. A shared home is what keeps
 * "is this blank?" and "what formatting survives?" from having two answers
 * (sharing.md § 1 — bidder-web must not import `core-admin`).
 *
 * Moved here in BL-1122; `@auctogon/core-admin/services/fine-print` and
 * `apps/admin-web/src/lib/html-sanitize.ts` re-export from this module so their
 * existing callers are unaffected.
 */
import sanitizeHtml from 'sanitize-html';

/**
 * Formatting-only allowlist — everything structural, scriptable, or remote is
 * dropped. Matches what the RichTextEditor can actually produce (text marks,
 * lists, links, colour, a YouTube embed), so sanitizing costs the operator
 * nothing they can type.
 *
 * The ONE allowlist (BUG-1941). The two exported sanitizers below differ only in
 * their `<a>` transform; neither may add a tag, attribute, class or scheme of its
 * own, or the bidder's toast and its tray row drift apart again.
 */
const RICH_TEXT_ALLOWLIST = {
    // `s` (strikethrough): StarterKit's Strike mark is on by default and not
    // disabled in RichTextEditor.tsx's config — no toolbar button, but its
    // paste rule matches `text-decoration: line-through`, so pasted
    // strikethrough text renders as `<s>` and must survive sanitizing too.
    allowedTags: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a', 'span', 'h3', 'h4', 'iframe'],
    // `target`/`rel` must be allowed here or the hardening in `transformTags`
    // below is added and then stripped straight back off by the allowlist.
    // `iframe`'s width/height/class are not needed here: every caller's own
    // wrapper CSS forces the embed's box (the `[&_iframe]:…` rules beside
    // `dangerouslySetInnerHTML`), same as it already does for `<a>`.
    //
    // `data-mention` / `data-own` are BL-2643's inert markers on a staff
    // @Inbox mention chip, baked into `body_html` at notification-send time
    // (`packages/notifications`). They carry no behavior — only which span is
    // a mention, and whether it names the reader.
    allowedAttributes: {
        a: ['href', 'target', 'rel'],
        span: ['style', 'data-mention', 'data-own'],
        iframe: ['src', 'width', 'height', 'allow', 'allowfullscreen', 'frameborder', 'title'],
    },
    // Only colour survives on a span — the editor's palette. Both patterns are
    // fully anchored: sanitize-html runs `regex.test(value)`, which is a
    // SUBSTRING match, so an unanchored pattern would let
    // `rgb(0,0,0) <anything>` ride along inside an allowed declaration.
    allowedStyles: {
        span: {
            color: [
                /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
                /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/,
            ],
        },
    },
    // The notification serializer's toast button, `<a class="toast-cta">`
    // (BL-1153), keeps its class so the bidder's toast styles it as a button.
    // Unstyled everywhere else (the tray, email, PDF), so it changes nothing
    // there.
    //
    // The `span` tokens are BL-2643's @Inbox mention chip (teal for a
    // teammate, warning-yellow for the reader's own name —
    // `mention-chip.ts` in `@auctogon/core-admin`). Listed by exact value
    // rather than imported: this package is shared with bidder-web and must
    // not depend on an admin-only package (sharing.md § 1). Each token is a
    // cosmetic Tailwind utility; none is scriptable.
    allowedClasses: {
        a: ['toast-cta'],
        span: [
            'rounded',
            'px-1',
            'font-medium',
            'bg-[var(--teal-100)]',
            'text-[var(--teal-800)]',
            'bg-[var(--warning-200)]',
            'text-[var(--ink-900)]',
        ],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: { iframe: ['https'] },
    // BL-1234's Youtube extension is hardcoded `nocookie: true`, so this is the
    // only host the editor can ever emit — narrower than the bidder's own
    // sanitizer (`apps/bidder-web/src/lib/sanitize-config.ts`), which also
    // allows `www.youtube.com` for a reason specific to that surface. A bare
    // `<iframe>` allowance with no host lock is a clickjacking/XSS hole
    // (BUG-2192); this is the gate that keeps it closed while still letting the
    // one embed the editor can author through.
    allowedIframeHostnames: ['www.youtube-nocookie.com'],
} satisfies sanitizeHtml.IOptions;

const RICH_TEXT_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
    ...RICH_TEXT_ALLOWLIST,
    transformTags: {
        // Force link safety: these open outside our control (mail client, PDF reader).
        a: sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' }),
    },
};

const IN_APP_RICH_TEXT_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
    ...RICH_TEXT_ALLOWLIST,
    transformTags: {
        // The target is left as authored, so an in-app link (the toast CTA)
        // navigates in place. Tab-napping defense: any target that escapes the
        // current context (`_blank`, `_top`, `_parent`, a named frame) gets
        // `rel="noopener noreferrer"` so the opened page can't reach `window.opener`.
        a: (tagName, attribs) => {
            const target = attribs.target ?? '';
            const opensNewContext = target !== '' && target !== '_self';
            return { tagName, attribs: opensNewContext ? { ...attribs, rel: 'noopener noreferrer' } : attribs };
        },
    },
};

/**
 * Strip everything but safe formatting from operator-authored rich text.
 * Every link is forced into a new tab, for surfaces that open outside our
 * control (email, PDF) and for the bidder's Notifications tray.
 *
 * @param html - raw editor HTML (may be null/undefined)
 * @returns HTML safe to splice into an email body or an `innerHTML` sink.
 */
export function sanitizeRichTextHtml(html: string | null | undefined): string {
    if (!html) return '';
    return sanitizeHtml(html, RICH_TEXT_SANITIZE_OPTIONS);
}

/**
 * The in-app variant of {@link sanitizeRichTextHtml} (BUG-1941): the same
 * allowlist, but link targets are left as authored, so an in-app link such as
 * the bidder toast's call-to-action opens in the same tab. Use it only where the
 * HTML renders inside the app; anything mailed or printed uses
 * {@link sanitizeRichTextHtml}.
 *
 * @param html - raw template / editor HTML (may be null/undefined)
 * @returns HTML safe for an `innerHTML` sink.
 */
export function sanitizeInAppRichTextHtml(html: string | null | undefined): string {
    if (!html) return '';
    return sanitizeHtml(html, IN_APP_RICH_TEXT_SANITIZE_OPTIONS);
}

/**
 * True when rich-text HTML carries no visible text.
 *
 * An emptied Tiptap field does not serialize to `''` — it emits `<p></p>`,
 * `<p><br></p>`, or a whitespace-only paragraph, all of which are truthy
 * strings that survive `.trim()`. Any "should this render?" decision must use
 * this check rather than raw truthiness, or a cleared field renders as an empty
 * heading with nothing under it.
 *
 * Safe because the editor's other marks (bold, italic, link, color) carry no
 * visible-text-free content of their own. A YouTube embed (BL-1234) is the one
 * exception: an iframe-only value strips to '', so a description or fine print
 * holding ONLY a video counts as empty by this check — pre-existing, not a
 * regression here, since this function's own logic is unchanged.
 * `ItemViewCard` DOES rely on this to decide whether its description section
 * renders at all, so a video-only item description renders no card today
 * (including hiding the video). Fixing that is a product decision about what
 * "empty" should mean for an embed-only value, out of scope for this file.
 */
export function isRichTextEmpty(html: string | null | undefined): boolean {
    if (!html) return true;
    const text = html
        .replace(/<[^>]*>/g, '') // strip all tags
        .replace(/&nbsp;/gi, ' ') // non-breaking spaces are whitespace here
        .replace(/&amp;/gi, '&') // an escaped ampersand is real content
        .trim();
    return text.length === 0;
}

const HTML_ESCAPES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

/**
 * Render author-typed PLAIN TEXT as the minimal HTML the tray renders — one
 * `<p>` with the text HTML-escaped and each line break as `<br />` (BL-1488).
 *
 * The clerk's broadcast composer is a textarea, not a rich-text editor:
 * `auction_broadcasts.body` is stored as typed, and this is the one place it
 * becomes `customer_in_app_notifications.body_html`. Escaping happens BEFORE
 * the break substitution, so nothing the author typed can become markup; the
 * result passes {@link sanitizeRichTextHtml} byte-for-byte unchanged (it emits
 * the sanitizer's own `<br />` spelling for exactly that reason), which the bidder's
 * `NotificationRow` applies at render (rule #18 — one allowlist, both apps).
 *
 * Trailing whitespace and CR/LF pairs are normalised; a blank input yields an
 * empty paragraph rather than throwing, so a caller that already validated
 * `min(1)` cannot be surprised here.
 *
 * @param text - the author's text.
 * @returns a single sanitizer-clean `<p>…</p>`.
 */
export function renderPlainTextAsHtml(text: string): string {
    const escaped = text
        .replace(/\r\n?/g, '\n')
        .trimEnd()
        .replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch] ?? ch);
    return `<p>${escaped.replace(/\n/g, '<br />')}</p>`;
}

const HTML_UNESCAPES: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
};

/**
 * The exact inverse of {@link renderPlainTextAsHtml} (BL-1491): the DM row
 * stores `body_html`, and the email/SMS legs of a clerk reply need the
 * author's text back — the email renderer escapes its own placeholders, so
 * handing it the stored markup would read `&amp;` to the bidder.
 *
 * Only undoes what the renderer did — the single `<p>` wrapper, the
 * `<br />` line breaks, the five escaped characters — so it is a round trip on
 * every input the renderer produces, and makes no attempt to be a general
 * HTML-to-text converter (which the tray's sanitizer allowlist would make a
 * much larger surface than any caller needs).
 *
 * @param html - a `renderPlainTextAsHtml` result.
 * @returns the text as the author typed it (CR/LF already normalised).
 */
export function renderedHtmlToPlainText(html: string): string {
    const inner = html.replace(/^<p>/, '').replace(/<\/p>$/, '');
    return inner
        .replace(/<br\s*\/?>/g, '\n')
        .replace(/&(amp|lt|gt|quot|#39);/g, (entity) => HTML_UNESCAPES[entity] ?? entity);
}

/**
 * A bare web address inside sanitizer-output TEXT (BL-2085): `http://` or
 * `https://`, then a run that excludes whitespace, `<`, `>` and `"` and stops
 * before the escaped `&lt;` / `&gt;` / `&quot;` / `&#39;` entities. `&amp;` is
 * allowed, so a query string stays one link. Only http(s) can match, which is
 * the whole scheme gate: `javascript:`, `mailto:` and `data:` never do.
 * `"` is excluded because sanitize-html decodes `&quot;` back to `"` in text,
 * and a `"` in the run would break out of the `href="…"` this emits.
 */
const BARE_URL = /https?:\/\/(?:(?!&lt;|&gt;|&quot;|&#39;)[^\s<>"])+/gi;

/** Punctuation that ends a sentence rather than a URL. */
const TRAILING_PUNCTUATION = /[.,;:!?'*]$/;

/** Closers trimmed off a URL's end only when the URL has no matching opener. */
const CLOSERS: Record<string, string> = { ')': '(', ']': '[' };

/** How many times `ch` occurs in `text`. */
function countOf(text: string, ch: string): number {
    return text.split(ch).length - 1;
}

/**
 * Split a matched run into the URL and the punctuation that follows it in the
 * sentence: a trailing `.` / `,` / `?` is the author's, and so is a `)` the URL
 * never opened — `(see https://x.com/a)` — while `…/Tractor_(farm)` keeps it.
 */
function splitTrailing(run: string): { url: string; trail: string } {
    let url = run;
    for (;;) {
        const last = url.slice(-1);
        const opener = CLOSERS[last];
        // A `;` that closes the escaped `&amp;` is part of the author's `&`,
        // not sentence punctuation — trimming it would print a stray `;`.
        if (url.endsWith('&amp;')) break;
        if (TRAILING_PUNCTUATION.test(url)) {
            url = url.slice(0, -1);
        } else if (opener != null && countOf(url, last) > countOf(url, opener)) {
            url = url.slice(0, -1);
        } else {
            break;
        }
    }
    return { url, trail: run.slice(url.length) };
}

/**
 * Wrap every bare http(s) URL in one TEXT segment with a new-tab anchor. A URL
 * that starts right after `=` (optionally `="` / `='`) is an attribute value
 * the author typed — `<a href="https://…">` arrives here escaped as text — and
 * stays plain (AC 6: typed markup stays plain, escaped text).
 */
function linkifyText(text: string): string {
    return text.replace(BARE_URL, (run: string, offset: number) => {
        if (/=["']?$/.test(text.slice(Math.max(0, offset - 2), offset))) return run;
        const { url, trail } = splitTrailing(run);
        if (!/^https?:\/\/./i.test(url)) return run;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${trail}`;
    });
}

/**
 * Linkify the text of SANITIZER OUTPUT (BL-2085). Tags pass through untouched;
 * text already inside an `<a>` is never re-wrapped. Module-private on purpose:
 * it is only sound on well-formed, sanitized HTML, so the one way in is
 * {@link renderMessageBodyHtml}.
 */
function linkifyHtml(html: string): string {
    let anchorDepth = 0;
    return html
        .split(/(<[^>]*>)/)
        .map((part) => {
            if (part.startsWith('<')) {
                if (/^<a[\s>]/i.test(part)) anchorDepth += 1;
                else if (/^<\/a\s*>/i.test(part)) anchorDepth = Math.max(0, anchorDepth - 1);
                return part;
            }
            return anchorDepth > 0 ? part : linkifyText(part);
        })
        .join('');
}

/**
 * The display HTML for a HUMAN-WRITTEN message body (BL-2085) — a direct
 * message either way, or a clerk broadcast: the shared allowlist, plus every
 * bare `http(s)://` URL turned into a link that opens in a new tab.
 *
 * It runs at RENDER, never at write: the stored `body_html` stays the delivery
 * record (ADR 056 — Delivery Record) and the exact input
 * {@link renderedHtmlToPlainText} round-trips for the email/SMS legs, and rows
 * written before BL-2085 link too, with no backfill. Sanitize → linkify →
 * sanitize: the first pass gives the tokenizer well-formed input, the second
 * re-checks every href's scheme and re-forces `target` / `rel` on the anchors
 * this added. A body with no URL comes out byte-for-byte as
 * {@link sanitizeRichTextHtml} alone renders it.
 *
 * @param html - a stored message body (`renderPlainTextAsHtml` output), or null.
 * @returns HTML safe for an `innerHTML` sink.
 */
export function renderMessageBodyHtml(html: string | null | undefined): string {
    const clean = sanitizeRichTextHtml(html);
    if (clean === '') return '';
    return sanitizeRichTextHtml(linkifyHtml(clean));
}

/** True for the first UTF-16 unit of a surrogate pair (half of an emoji, say). */
function isHighSurrogate(code: number): boolean {
    return code >= 0xd800 && code <= 0xdbff;
}

/** The ellipsis {@link truncatePreview} appends — one character, so it counts once against the cap. */
const PREVIEW_ELLIPSIS = '…';

/**
 * The marker {@link truncateSmsText} appends: three GSM-7 full stops. U+2026
 * is outside the GSM-7 alphabet, so '…' would turn a GSM-7 text UCS-2 and
 * roughly double its segment count (BUG-2607).
 */
const SMS_ELLIPSIS = '...';

/**
 * The shared one-line cut behind {@link truncatePreview} and
 * {@link truncateSmsText}: collapse newlines, and when the text is over `max`,
 * cut it so the result plus `marker` fits.
 *
 * The cut lands on the last word boundary unless that boundary keeps less than
 * `minWordCutFill` of the room, in which case the text is cut by length — a
 * long unbroken word is hard-cut rather than dropped whole (BUG-2607).
 */
function shortenOneLine(opts: {
    text: string;
    max: number;
    marker: string;
    /** Trailing characters stripped from the cut before the marker goes on. */
    trailing: RegExp;
    /** The fraction of the room a word-boundary cut must keep; 0 always prefers the boundary. */
    minWordCutFill: number;
}): string {
    const { text, max, marker, trailing, minWordCutFill } = opts;
    const oneLine = text.replace(/\s*\n\s*/g, ' ').trim();
    if (oneLine.length <= max) return oneLine;
    const room = max - marker.length;
    // A cap smaller than the marker leaves no room for text, so the result is
    // as much of the marker as fits.
    if (room <= 0) return marker.slice(0, Math.max(max, 0));
    const head = oneLine.slice(0, room + 1);
    const lastSpace = head.lastIndexOf(' ');
    // A cut by length backs off one unit rather than leave half of a
    // surrogate pair (an emoji) at the end (BUG-1822).
    const end = isHighSurrogate(oneLine.charCodeAt(room - 1)) ? room - 1 : room;
    const atWord = lastSpace > 0 && lastSpace >= room * minWordCutFill;
    const cut = atWord ? head.slice(0, lastSpace) : oneLine.slice(0, end);
    return `${cut.replace(trailing, '')}${marker}`;
}

/**
 * Shorten a message for a single-line preview (BL-1491 — the SMS leg of a
 * clerk reply): newlines collapse to spaces, and anything over `max` is cut at
 * the last word boundary that still fits an ellipsis inside the cap.
 *
 * @param text - the author's text.
 * @param max - the cap on the RESULT's length, ellipsis included.
 * @returns the preview, never longer than `max`.
 */
export function truncatePreview(text: string, max: number): string {
    return shortenOneLine({ text, max, marker: PREVIEW_ELLIPSIS, trailing: /[\s,;:—–-]+$/, minWordCutFill: 0 });
}

/**
 * Shorten author-typed prose to fit an SMS budget (BUG-2607 — the clerk
 * broadcast's `{BroadcastMessage}`). Like {@link truncatePreview}, but built
 * for the SMS bill:
 * - it ends with GSM-7 `...`, never `…`, so a GSM-7 text stays GSM-7;
 * - it cuts at a word boundary only when that keeps at least half the room,
 *   so one long unbroken word is hard-cut to fill the budget, not dropped.
 *
 * @param text - the author's text.
 * @param max - the cap on the RESULT's length, marker included.
 * @returns the shortened text, never longer than `max`.
 */
export function truncateSmsText(text: string, max: number): string {
    return shortenOneLine({ text, max, marker: SMS_ELLIPSIS, trailing: /[\s,;:.—–-]+$/, minWordCutFill: 0.5 });
}
